/**
 * CaptureLine — o rastro da captura conversacional no fio (spec
 * `2026-08-14-aba-acoes-conversa-em-curso-design.md` §5.1; mockup `2b`).
 *
 * Quando o motor lê uma fala e registra a promessa sozinho, a conversa mostra uma
 * linha discreta logo abaixo da mensagem que a originou — com `desfazer`. É o que
 * torna a captura automática honesta: o operador VÊ que algo foi registrado, no
 * instante e no lugar em que aconteceu, e desfaz sem sair dali.
 *
 * Alinhada ao lado de quem prometeu (direita para a fala do atendimento, esquerda
 * para a do cliente), monoespaçada e sem caixa — ela informa, não compete com a
 * mensagem.
 *
 * ⚠️ Largura: o painel tem 360px e a coluna do fio é estreita. A copy é curta de
 * propósito — na primeira versão do mockup a linha vazou para fora e o `desfazer`
 * ficou cortado.
 */

import { provenanceOf } from '@lume/shared';

import { useUpdatePendency } from '../../../hooks/use-pendencies';

import type { PendencyFixture } from '../hooks/use-panel-fixtures';

export function CaptureLine({ pendency }: { pendency: PendencyFixture }): JSX.Element {
  const update = useUpdatePendency();
  const prov = provenanceOf(pendency.source, pendency.aiAgentId);
  const fromClient = prov.who === 'client';

  return (
    <div
      data-testid={`capture-line-${pendency.sourceMessageId ?? pendency.id}`}
      className="flex items-center gap-1.5"
      style={{
        maxWidth: '100%',
        alignSelf: fromClient ? 'flex-start' : 'flex-end',
        justifyContent: fromClient ? 'flex-start' : 'flex-end',
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--text-2xs)',
        color: 'var(--fg-3)',
        padding: '2px 4px',
      }}
    >
      <span
        aria-hidden
        style={{ width: 14, height: 1, background: 'var(--ai-border)', flex: 'none' }}
      />
      <span style={{ color: 'var(--ai-fg)', flex: 'none' }} aria-hidden>
        ◆
      </span>
      <span>{label(fromClient, pendency.dueAt)}</span>
      <button
        type="button"
        onClick={() =>
          update.mutate({
            pendencyId: pendency.id,
            expectedVersion: pendency.version,
            body: { status: 'cancelled' },
          })
        }
        disabled={update.isPending}
        style={{
          flex: 'none',
          color: 'var(--brand)',
          background: 'none',
          border: 0,
          padding: 0,
          cursor: 'pointer',
          textDecoration: 'underline',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--text-2xs)',
        }}
      >
        desfazer
      </button>
    </div>
  );
}

/**
 * Copy curta. Sem prazo, NÃO inventa um: diz só que registrou. O prazo que o motor
 * não achou é ausência real, e escrever «para sempre» ou uma data qualquer aqui
 * seria a mesma mentira que o «até » vazio do cartão.
 */
function label(fromClient: boolean, dueAt: string): string {
  const who = fromClient ? 'ele prometeu' : 'registrado';
  return dueAt.length > 0 ? `${who} · ${dueAt}` : who;
}
